import { useEffect, useMemo, useState } from "react";
import { ArrowUpRight, Search } from "lucide-react";
import GroupHeader from "@/components/GroupHeader";
import GroupFooter from "@/components/GroupFooter";
import SEO from "@/components/SEO";
import ClosingCTA from "@/components/site/ClosingCTA";
import { Eyebrow, OutLink, Reveal } from "@/components/site/Bits";
import { track, useSettings } from "@/lib/site";
import { EDITIONS_URL, EDITION_URL, formatDate } from "@/components/GetThePlotFeed";

const cleanLabel = (l) => (l || "").replace(/^[^\p{L}\p{N}]+/u, "").trim();
const PAGE = 12;

function EditionCard({ e }) {
    return (
        <a href={EDITION_URL(e.date)} target="_blank" rel="noopener noreferrer" onClick={() => track("news_click", e.date)}
           data-testid={`news-card-${e.date}`}
           className="group flex flex-col rounded-2xl bg-cream border border-hairline p-7 min-h-[260px] hover:border-forest hover:-translate-y-0.5 transition-[border-color,transform] duration-300">
            <p className="mono-label text-moss mb-5">{formatDate(e.date)}</p>
            <p className="font-serif text-2xl text-forest leading-snug mb-4 line-clamp-4">{e.title}</p>
            {e.hook && <p className="text-sm text-muted_text leading-relaxed line-clamp-3">{e.hook}</p>}
            <div className="mt-auto pt-6 flex flex-wrap gap-2">
                {e.cats.slice(0, 3).map((c) => <span key={c} className="rounded-full bg-surface px-3 py-1 text-[11px] text-muted_text">{c}</span>)}
            </div>
            <span className="mt-5 inline-flex items-center gap-2 mono-label text-forest group-hover:text-moss">Read edition <ArrowUpRight className="h-4 w-4" /></span>
        </a>
    );
}

export default function News() {
    const s = useSettings();
    const [editions, setEditions] = useState(null);
    const [q, setQ] = useState("");
    const [cat, setCat] = useState("All");
    const [limit, setLimit] = useState(PAGE);

    useEffect(() => {
        fetch(EDITIONS_URL, { cache: "no-store" }).then((r) => r.json()).then((d) => {
            const list = (Array.isArray(d) ? d : d.editions || []).map((e) => {
                const items = (e.sections || []).flatMap((sec) => (sec.items || []).map((it) => `${it.headline} ${it.summary || ""}`));
                return { ...e, cats: [...new Set((e.sections || []).map((sec) => cleanLabel(sec.label)).filter(Boolean))], text: `${e.title} ${e.hook || ""} ${e.summary || ""} ${items.join(" ")}`.toLowerCase() };
            });
            setEditions(list);
        }).catch(() => setEditions([]));
    }, []);

    const cats = useMemo(() => {
        const counts = {};
        (editions || []).forEach((e) => e.cats.forEach((c) => { counts[c] = (counts[c] || 0) + 1; }));
        return Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([c]) => c);
    }, [editions]);

    const filtered = (editions || []).filter((e) => (cat === "All" || e.cats.includes(cat)) && (!q || e.text.includes(q.toLowerCase())));

    return (
        <div data-testid="news-page" className="bg-cream text-ink min-h-screen">
            <SEO path="/news" og="news" title="News — Get The Plot, daily Portuguese real estate briefing"
                 description="Get The Plot by Verde 2050: daily news on Portuguese real estate, planning and licensing, investment trends, market updates and regulation." />
            <GroupHeader alwaysDark />
            <section className="bg-forest text-cream pt-36 md:pt-44 pb-16 md:pb-20">
                <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-10 items-end">
                    <div className="md:col-span-8">
                        <Reveal><Eyebrow dark>News · Get The Plot</Eyebrow></Reveal>
                        <Reveal delay={100}><h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.04] tracking-tight mt-8">The daily briefing on <em className="italic text-sage">Portuguese real estate.</em></h1></Reveal>
                        <Reveal delay={200}><p className="mt-8 text-base md:text-lg text-cream/80 max-w-2xl">Developments, planning and licensing, investment trends, market updates and regulation — plus news from our own projects.</p></Reveal>
                    </div>
                    <Reveal delay={300} className="md:col-span-4 md:justify-self-end">
                        <OutLink href={s.news_url} event="news_subscribe_click" label="news-page" testid="news-subscribe"
                                 className="rounded-full bg-cream text-forest hover:bg-sage px-7 py-4 text-sm font-medium transition-colors">Subscribe — it's free</OutLink>
                    </Reveal>
                </div>
            </section>

            <section className="sticky top-[72px] z-30 bg-cream/90 backdrop-blur-xl border-b border-hairline">
                <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex flex-col lg:flex-row lg:items-center gap-4">
                    <label className="relative lg:w-80 shrink-0">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted_text" />
                        <input value={q} onChange={(e) => { setQ(e.target.value); setLimit(PAGE); }} placeholder="Search news" aria-label="Search news"
                               data-testid="news-search" className="editorial-input !rounded-full !pl-11 !py-2.5" />
                    </label>
                    <div className="flex gap-2 overflow-x-auto" data-testid="news-categories">
                        {["All", ...cats].map((c) => (
                            <button key={c} type="button" onClick={() => { setCat(c); setLimit(PAGE); }} aria-pressed={cat === c}
                                    data-testid={`news-cat-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                    className={`shrink-0 rounded-full border px-4 py-2 text-sm transition-colors ${cat === c ? "bg-forest border-forest text-cream" : "border-hairline hover:border-forest"}`}>
                                {c}
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-16 md:py-24">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    {editions === null && <div className="grid md:grid-cols-3 gap-6">{[0, 1, 2].map((i) => <div key={i} className="h-64 rounded-2xl bg-surface animate-pulse" />)}</div>}
                    {editions && filtered.length === 0 && <p className="text-muted_text" data-testid="news-empty">No editions match your search.</p>}
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="news-grid">
                        {filtered.slice(0, limit).map((e) => <EditionCard key={e.date} e={e} />)}
                    </div>
                    {filtered.length > limit && (
                        <div className="mt-14 text-center">
                            <button type="button" onClick={() => setLimit((l) => l + PAGE)} data-testid="news-load-more"
                                    className="rounded-full border border-forest/40 px-7 py-4 text-sm text-forest hover:bg-forest hover:text-cream transition-colors">Load more</button>
                        </div>
                    )}
                </div>
            </section>
            <ClosingCTA testid="news-contact" />
            <GroupFooter />
        </div>
    );
}
